<script setup lang="ts">
import Filter from "@/features/shifts/ui/filter.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import {useShiftStore} from "@/features/shifts/shifts.store";
import {IShift} from "@/features/shifts/shifts.types";
import {onMounted, ref, watch} from "vue";
import {Trash01, Plus} from "utruckin-icons";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import Checkbox from "@/features/shifts/ui/checkbox.vue";
import {useAlert} from "@/utils/alert";
import {useAppStore} from "@/store/app.store";
import CustomBadge from "@/components/custom/CustomBadge.vue";

const shiftStore = useShiftStore();
const appStore = useAppStore();
const {show} = useAlert()

const loading = ref<boolean>(true);
const list = ref<IShift[]>([]);

const editingModal = ref<boolean>(false);
const modalTitle = ref<string>("");
const modalItem = ref<IShift>({
  name: "",
  title: "",
  id: "0",
  start_time: "",
  end_time: "",
  is_active: false,
});

const isStartTimeComplete = ref(true)
const isEndTimeComplete = ref(true)

const deletingModal = ref<boolean>(false);
const deleteModalItem = ref<any>()

const getList = async () => {
  list.value = await shiftStore.getShifts(loading);
}

const edit = async (item?: IShift) => {
  if (item) {
    let newItem = await shiftStore.getShiftById(loading, item.id);
    modalTitle.value = 'Edit shift';
    modalItem.value = newItem
    editingModal.value = true;
  } else {
    modalTitle.value = 'Add shift';
    editingModal.value = true;
  }
}

const openDeleteModal = () => {
  deleteModalItem.value = modalItem.value;
  editingModal.value = false;
  deletingModal.value = true;
}

const deleteShift = async () => {
  let response = await shiftStore.deleteShift(loading, deleteModalItem.value.id);

  if (response.status === "ok") {
    show("Deleted successfully", "success");
    cancelDeleteButton()
    cancelEditButton()
    appStore.triggerRefresh()
  }
}

const cancelEditButton = () => {
  editingModal.value = false;
  closeModal()
}

const cancelDeleteButton = () => {
  deletingModal.value = false;
}

const saveEditButton = async () => {
  if (!modalItem.value.name && !modalItem.value.title && !modalItem.value.start_time && !modalItem.value.end_time) return
  let result;
  if (+modalItem.value.id) {
    let newRequest: any = {...modalItem.value}
    result = await shiftStore.updateShift(loading, newRequest, newRequest.id);
  } else {
    let newRequest: any = {...modalItem.value}
    delete newRequest.id;
    result = await shiftStore.createShift(loading, newRequest);
  }
  if (result) {
    show("Saved successfully", "success");
    cancelEditButton()
    appStore.triggerRefresh()
  }
}

const closeModal = () => {
  modalTitle.value = '';
  modalItem.value = {
    name: "",
    title: "",
    id: "0",
    start_time: "",
    end_time: "",
    is_active: false,
  };
}

const closeDeleteModal = () => {
  deleteModalItem.value = undefined;
}

const onStartTimeCompleted = (v: boolean) => {
  isStartTimeComplete.value = v
}

const onEndTimeCompleted = (v: boolean) => {
  isEndTimeComplete.value = v
}

onMounted(() => {
  getList()
})

watch(
    () => appStore.refreshKey,
    () => {
      getList()
    },
    {
      immediate: false,
    }
)

watch(
    () => modalItem.value.title,
    (newVal) => {
      modalItem.value.name = newVal.trim().split(" ").join("_").toLowerCase();
    }
)
</script>

<template>
  <div class="v-shifts">
    <!--    <Filter @clicked-add="edit"/>-->

    <!--    <div class="body">-->
    <!--      <CommonTable :headers="shiftStore.shiftHeaders" :items="list">-->

    <!--        <template #id="{index}">-->
    <!--          {{ index + 1 }}-->
    <!--        </template>-->

    <!--        <template #is_active="{item}">-->
    <!--          <CustomBadge-->
    <!--              :title="item['is_active'] ? 'Active' : 'Inactive'"-->
    <!--              :type="item['is_active'] ? 'success' : 'danger'"-->
    <!--          ></CustomBadge>-->
    <!--        </template>-->

    <!--        <template #actions="{item}">-->
    <!--          <div class="actions">-->
    <!--            <div class="actions__edit" @click="edit(item)">-->
    <!--              <Edit02 :size="16"/>-->
    <!--            </div>-->
    <!--            <div class="actions__delete" @click="openDeleteModal(item)">-->
    <!--              <Trash01 :size="16"/>-->
    <!--            </div>-->
    <!--          </div>-->
    <!--        </template>-->
    <!--      </CommonTable>-->
    <!--    </div>-->


    <div class="card" v-for="(item, index) in list" :key="index" @click="edit(item)">
      <div class="card__header">
        <div class="card__header--title">{{ item.title }}</div>
        <div class="card__header--status">
          <CustomBadge
              :title="item['is_active'] ? 'Active' : 'Inactive'"
              :type="item['is_active'] ? 'success' : 'danger'"
          ></CustomBadge>
        </div>
      </div>

      <div class="card__body">
        <div class="card__body--time">
          <div class="label">Start time</div>
          <div class="time">{{ item.start_time }}</div>
        </div>
        <div class="card__body--time">
          <div class="label">End time</div>
          <div class="time">{{ item.end_time }}</div>
        </div>
      </div>
    </div>

    <div class="card add" @click="edit(undefined)">
      <div class="add__card--icon">
        <Plus :size="24"/>
      </div>
    </div>
  </div>

  <CommonModal v-model="editingModal" :title="modalTitle" @close="closeModal">
    <div>
      <CommonInput
          label="Shift name"
          placeholder="Shift name"
          v-model:model-value="modalItem.title"
          :error="!modalItem.title"
          errorMessage="Value is required"
      />
      <CommonInput
          label="Start time"
          placeholder="Start time"
          mask="##:##:##"
          v-model="modalItem.start_time"
          :error="!modalItem.start_time || !isStartTimeComplete"
          errorMessage="Value is required"
          @mask:completed="onStartTimeCompleted"
      />
      <CommonInput
          label="End time"
          placeholder="End time"
          mask="##:##:##"
          v-model:model-value="modalItem.end_time"
          :error="!modalItem.end_time || !isEndTimeComplete"
          errorMessage="Value is required"
          @mask:completed="onEndTimeCompleted"
      />

      <Checkbox v-model:model-value="modalItem.is_active" label="Active"/>
    </div>

    <template #footer>
      <div class="modal__footer">
        <div class="modal__footer--delete" @click="openDeleteModal">
          <Trash01 :size="24"/>
        </div>
        <div class="modal__footer--actions">
          <CommonButton type="secondary" label="Cancel" @clicked="cancelEditButton"/>
          <CommonButton
              :is-disabled="
            !modalItem.name ||
            !modalItem.title ||
            !modalItem.start_time ||
            !modalItem.end_time ||
            !isStartTimeComplete ||
            !isEndTimeComplete"
              type="primary"
              label="Save"
              @clicked="saveEditButton"
          />
        </div>
      </div>
    </template>
  </CommonModal>

  <CommonModal v-model="deletingModal" @close="closeDeleteModal">
    <div class="delete__title">
      Are you sure you want to delete this shift? This action cannot be undone.
    </div>

    <template #footer>
      <div class="modal__footer">
        <CommonButton type="secondary" label="Cancel" @clicked="cancelDeleteButton"/>
        <CommonButton type="primary" label="Save" @clicked="deleteShift"/>
      </div>
    </template>
  </CommonModal>
</template>

<style scoped lang="scss">
.v-shifts {
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  .card {
    width: calc((100% - 48px) / 5);
    border: 1px solid var(--color-border);
    background: var(--color-bg-tertiary);
    border-radius: 16px;
    padding: 12px;
    cursor: pointer;
    min-width: 300px;
    height: 118px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      border-radius: 12px;
      border: 1px solid var(--color-border);
      background: var(--color-background);
      margin-bottom: 12px;
      color: var(--color-label);

      .title {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0;
        font-weight: 600;
        color: var(--color-label);
      }
    }

    &__body {
      display: flex;
      gap: 16px;
      background: transparent;

      &--time {
        width: calc((100% - 32px) / 3);
        background: transparent;

        .label {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: 0;
          color: var(--color-secondary-label);
          font-weight: 600;
          background: transparent;
        }

        .time {
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0;
          color: var(--color-label);
          font-weight: 600;
          background: transparent;
        }
      }
    }

    &.add {
      display: flex;
      justify-content: center;
      align-items: center;
      border-style: dashed;

      .add__card--icon {
        background: transparent;
      }
    }
  }

  .body {
    padding: 0 16px;

    .actions {
      display: flex;
      align-items: center;
      gap: 6px;

      &__edit, &__delete {
        width: 31px;
        height: 31px;
        border-radius: 50%;
        border: 1px solid var(--color-border);
        background-color: var(--color-bg-secondary);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        &:hover {
          background-color: var(--color-bg-tertiary);
        }
      }
    }
  }
}

.delete__title {
  color: var(--color-label);
}

.modal__footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--delete {
    padding: 12px;
    background: var(--color-bg-secondary);
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-label);
  }

  &--actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }
}
</style>